import { AssetType, ManualBrokerID } from 'config/consts';
import * as consts from 'config/consts';
import React, { useState } from 'react';
import { CurrencyMode, useCurrencyMode } from 'hooks/useCurrencyMode';
import { useModal } from 'hooks/useModal';
import { useRouting } from 'hooks/useRouting';
import { GetUserBrokerResultItem } from 'config/api';
import { formatNumber } from 'utils/formatter';
import { WhatIsOtherModal } from 'components/features/modal/WhatIsOtherModal';
import {
  AboutExchangeRateModalButton,
  DataFetchingState,
} from '../PortfolioStuffing/asset/PortfolioStuffingAsset';
import { StopAggregate } from '../PortfolioStuffing/asset/AssetOrganizedPortfolioForeign';
import { DataFetching } from '../PortfolioStuffing/asset/AssetOrganizedPortfolioItem';
import { WhatIsIncludeAsstsModalButton } from '../PortfolioStuffing/asset/AssetOrganizedPortfolioContainer';
import { SelectedChartTypeProps, TypeKey } from './PortfolioRatioStuffing';
import { getTypeKeyToString } from './PortfolioRatioChartInternal';
import {
  PortfolioRatioItemProps,
  RawDataPositions,
} from './PortfolioRatioStuffingInternal';
import {
  isForeignStockTypes,
  isNotExistDetailTypes,
  productTypesToColor,
  productTypesToStringLabel,
} from 'components/features/utils/productTypes';
import {
  AccountType,
  accountTypeToString,
  isNisaTypes,
} from 'components/features/utils/accountType';
import {
  amountToAmountColor,
  isSymbolEmpty,
} from 'components/features/utils/util';
import { commaFormat } from './../../new-report/utils/formatter';

export const getDataFetchingTextStyle_ = (
  dataFetchingState: DataFetchingState
) => (defaultTextStyle: string) =>
  dataFetchingState === 'Fetching' ? 'text-bluegray-300' : defaultTextStyle;

const PortfolioRatioItemPosition = ({
  position,
  isLastLength,
  currencyMode,
  selectedType,
  brokersInfo,
}: {
  position: RawDataPositions;
  isLastLength: boolean;
  currencyMode: CurrencyMode;
  selectedType: SelectedChartTypeProps;
  brokersInfo: GetUserBrokerResultItem[];
}) => {
  const { pushRouting } = useRouting();
  const isStockMargin = position.cashMargin === consts.CashMargin.信用;
  const isManual = position.brokerID === ManualBrokerID.手入力口座;

  // MEMO: 『類推中』はMONO-***、『類推完了』はDC-***で返却
  const isInvestmentTrust =
    position.productType === consts.productTypes.投資信託;
  const isDataCooperation = isInvestmentTrust && /^MONO/.test(position.symbol);
  const isAnalogyComplete =
    isInvestmentTrust &&
    (position.symbol === '' || /^DC/.test(position.symbol));

  const goDetail = () => {
    if (isManual && isNotExistDetailTypes(position.productType)) return;

    let link = null;
    if (isInvestmentTrust) {
      if (isDataCooperation) {
        link = `/accountdetail/${position.account}`;
      } else if (!isAnalogyComplete) {
        link = `/tradedetail/${position.symbol}`;
      }
    } else {
      if (!isSymbolEmpty(position.symbol)) {
        link = `/tradedetail/${position.symbol}`;
      }
    }

    if (!isNotExistDetailTypes(position.productType) && link !== null) {
      pushRouting({ path: link }, true, window.scrollY);
    }
  };

  let currentAmount = null;
  let profitOrLoss = null;
  let unit = '';
  if (!isForeignStockTypes(position.productType)) {
    currentAmount = position.currentAmount;
    profitOrLoss = position.profitOrLoss;
    unit = '円';
  } else {
    if (currencyMode === 'JPY') {
      currentAmount = position.currentAmount;
      profitOrLoss = position.profitOrLoss;
      unit = '円';
    } else {
      if (isForeignStockTypes(position.productType)) {
        currentAmount = position.currentAmountUSD;
        profitOrLoss = position.profitOrLossUSD;
        unit = position.currencyUnit ?? '';
      } else {
        currentAmount = null;
        profitOrLoss = null;
        unit = '';
      }
    }
  }

  const brokerType = brokersInfo.find(
    brokers => brokers.Account === position.account
  )?.BrokerType;

  let text = '';
  let color = '';
  let bgColor = '';

  switch (selectedType) {
    case 'products':
      text = productTypesToStringLabel(
        position.productType,
        position.cashMargin
      );
      color = 'text-white';
      bgColor = productTypesToColor(position.productType, position.cashMargin);
      break;
    case 'account':
      if (position.accountType === consts.AccountType.一般) {
        text = accountInfoList.General.text;
        color = accountInfoList.General.color;
        bgColor = accountInfoList.General.bgColor;
      } else if (position.accountType === consts.AccountType.特定) {
        text = accountInfoList.Identification.text;
        color = accountInfoList.Identification.color;
        bgColor = accountInfoList.Identification.bgColor;
      } else if (isNisaTypes(position.accountType)) {
        const accountType = position.accountType as AccountType;
        text = accountTypeToString(accountType);
        color = accountInfoList.Nisa.color;
        bgColor = accountInfoList.Nisa.bgColor;
      } else if (
        (position.accountType === consts.AccountType.iDeCo ||
          position.accountType === consts.AccountType.企業型) &&
        brokerType !== undefined &&
        brokerType === consts.BrokerType.確定拠出年金
      ) {
        text = accountInfoList.DefinedContributionPension.text;
        color = accountInfoList.DefinedContributionPension.color;
        bgColor = accountInfoList.DefinedContributionPension.bgColor;
        // TODO: 口座詳細で口座種別『その他』の場合は口座種別タグは非表示としているため仕様を合わせる
        // また、上記以外の例外も口座種別タグは非表示とする
      } else {
        text = '';
        color = '';
        bgColor = '';
      }
      break;
  }

  const profitOrLossColor =
    profitOrLoss === 0 || profitOrLoss === null
      ? 'text-bluegray-700'
      : amountToAmountColor(profitOrLoss);

  const refresh = position.refresh;

  const isGoDetail =
    !isNotExistDetailTypes(position.productType) &&
    position.symbol !== null &&
    position.symbol !== '' &&
    !isAnalogyComplete
      ? true
      : false;

  return (
    <>
      <div
        className={`py-4 pl-4 border-bluegray-300 ${
          isGoDetail ? 'cursor-pointer' : ''
        } ${
          isLastLength ? 'border rounded-b-lg' : 'border-t border-l border-r'
        }`}
        onClick={(): void => goDetail()}>
        <div className="flex">
          <div className="flex-1">
            <div>
              {isDataCooperation && (
                <>
                  <DataFetching className="mb-3" />
                </>
              )}
              {refresh && (
                <StopAggregate
                  {...{
                    refresh,
                  }}
                />
              )}
            </div>
            <div className="flex flex-row items-center">
              <div
                className={
                  color && bgColor
                    ? `h-4 text-1.5xs px-3 text-center justify-center flex items-center rounded-full ${color} ${bgColor}`
                    : ''
                }>
                {text}
              </div>
              <div
                className={`ml-2 ${isManual ? 'text-xs' : 'text-sm'} ${
                  isDataCooperation ? 'text-bluegray-300' : 'text-bluegray-700'
                }`}>
                {!isManual ? position.symbol : '直接入力'}
              </div>
            </div>
            <div
              className={`flex flex-row mt-1 ${
                isDataCooperation ? 'text-bluegray-300' : 'text-bluegray-700'
              }`}>
              <div className="line-clamp-2 mr-2 text-sm font-bold">
                {position.symbolName}
              </div>
              {!isStockMargin ? (
                <div
                  className="flex flex-row items-end ml-auto flex-shrink text-sm font-bold"
                  style={{ whiteSpace: 'nowrap' }}>
                  {currentAmount !== null
                    ? commaFormat(currentAmount, {
                        numberOfDecimals:
                          currencyMode === 'FOREIGN' &&
                          isForeignStockTypes(position.productType)
                            ? 2
                            : 0,
                      })
                    : '---'}
                  <span className="ml-1">{unit}</span>
                </div>
              ) : (
                <></>
              )}
            </div>
            {!isManual ? (
              <div
                className={`flex flex-row justify-end ml-auto text-xs mt-1 ${
                  isDataCooperation ? 'text-bluegray-300' : profitOrLossColor
                }`}>
                <div
                  className={`${isDataCooperation ? '' : 'text-bluegray-700'}`}>
                  評価損益
                </div>
                <div className="ml-2">
                  {profitOrLoss !== null
                    ? formatNumber(profitOrLoss, {
                        numberOfDecimal:
                          currencyMode === 'FOREIGN' &&
                          isForeignStockTypes(position.productType)
                            ? 2
                            : 0,
                        withPlusSign: true,
                      })
                    : '---'}
                </div>
                <span className="ml-1">{unit}</span>
              </div>
            ) : (
              <></>
            )}
          </div>
          <div className="flex items-center justify-center w-10">
            {isGoDetail ? (
              <div className="icon-chevron-right flex text-2xl text-gray-500"></div>
            ) : (
              <></>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

const getColorClass = (type: TypeKey): string => {
  switch (type) {
    case AssetType['国内現物']:
      return 'bg-dot-Stock';
    case AssetType['信用']:
      return 'bg-dot-Margin';
    case AssetType['外国株式']:
      return 'bg-dot-ForeignStock';
    case AssetType['ロボアドバイザー']:
      return 'bg-dot-Robo';
    case AssetType['投資信託']:
      return 'bg-dot-InvestmentTrust';
    case AssetType['預金_MRF']:
      return 'bg-dot-Cash';
    case AssetType['その他']:
      return 'bg-dot-Other';
    case AssetType['未上場株式']:
      return 'bg-dot-UnlistedStocks';
    case AssetType['国内ETF・ETN']:
      return 'bg-dot-EtfEtn';
    case AssetType['外国ETF・ETN']:
      return 'bg-dot-ForeignEtfEtn';
    case AssetType['国内REIT']:
      return 'bg-dot-Reit';
    case AssetType['外国REIT']:
      return 'bg-dot-ForeignReit';
    case AssetType['国内債券']:
      return 'bg-dot-Bond';
    case AssetType['外国債券']:
      return 'bg-dot-ForeignBond';
    case AssetType['国内EB']:
      return 'bg-dot-Eb';
    case AssetType['外国EB']:
      return 'bg-dot-ForeignEb';
    case AssetType['不動産']:
      return 'bg-dot-RealEstate';
    case 'General':
      return 'bg-dot-General';
    case 'Identification':
      return 'bg-dot-Identification';
    case 'Nisa':
      return 'bg-dot-Nisa';
    case 'DefinedContributionPension':
      return 'bg-dot-DefinedContributionPension';
    case 'Other':
      return 'bg-dot-Other';
    case 'JP_EQ':
      return 'bg-dot-JP_EQ';
    case 'DC_EQ':
      return 'bg-dot-DC_EQ';
    case 'EM_EQ':
      return 'bg-dot-EM_EQ';
    case 'JP_FI':
      return 'bg-dot-JP_FI';
    case 'DC_FI':
      return 'bg-dot-DC_FI';
    case 'EM_FI':
      return 'bg-dot-EM_FI';
    case 'JP_REIT':
      return 'bg-dot-JP_REIT';
    case 'G_REIT':
      return 'bg-dot-G_REIT';
    case 'OTHER':
      return 'bg-dot-OTHER';
  }
  return '';
};

export const ToggleButton = ({
  currencyMode,
  toggle,
}: {
  currencyMode: CurrencyMode;
  toggle: () => void;
}) => {
  const toggleIconImage = `/static/images/${
    currencyMode === 'JPY' ? 'jpn' : 'foreign'
  }_change.svg`;
  return (
    <img
      src={toggleIconImage}
      className="ml-2 h-6 cursor-pointer"
      alt="切り替えアイコン"
      onClick={e => {
        e.stopPropagation();
        toggle();
      }}></img>
  );
};

export const PortfolioRatioStuffingList = ({
  typeKey,
  amount,
  ratio,
  positions,
  selectedType,
  brokersInfo,
}: PortfolioRatioItemProps & {
  selectedType: SelectedChartTypeProps;
  brokersInfo: GetUserBrokerResultItem[];
}): React.ReactElement => {
  const [isShowPositions, setIsShowPositions] = useState<boolean>(false);
  const { currencyMode, toggle } = useCurrencyMode();
  const [isShowModal, { hideModal, showModal }] = useModal();

  const color = getColorClass(typeKey);
  const title = getTypeKeyToString(typeKey);

  const isEmptyPositions = positions.length === 0;

  const isHoldForeignStock = positions.some(position =>
    isForeignStockTypes(position.productType)
  );

  const changeIsShowPositions = (): void => {
    if (!isEmptyPositions) {
      setIsShowPositions(!isShowPositions);
    }
  };

  const isPositionsSameCurrencyUnit =
    positions.length !== 0 &&
    positions.every(
      position => position.currencyUnit === positions[0].currencyUnit
    );
  const commonUnit = isPositionsSameCurrencyUnit
    ? positions[0].currencyUnit
    : null;

  const amountUSD =
    currencyMode === 'JPY'
      ? 0
      : isPositionsSameCurrencyUnit
      ? positions.reduce((acc, value) => {
          const num = isForeignStockTypes(value.productType)
            ? value.currentAmountUSD ?? 0
            : 0;
          return acc + num;
        }, 0)
      : null;

  return (
    <>
      <div className="mb-2">
        <div className={`${isEmptyPositions ? '' : 'cursor-pointer'}`}>
          <div
            className={`flex flex-row border-bluegray-300 py-4 pl-4 ${
              isShowPositions
                ? 'border-t border-l border-r rounded-t-lg'
                : 'border rounded-lg'
            }`}
            onClick={(): void => changeIsShowPositions()}>
            <div className="flex flex-col">
              <div className="flex items-center mb-1">
                <div className={`w-4 h-4 rounded-full ${color} mr-2`}></div>
                <div className="flex flex-row text-lg font-bold text-bluegray-700">
                  {title}
                </div>
              </div>
              <div className="flex leading-6 text-bluegray-700 pl-6">
                <div className="text-base mr-1">
                  {currencyMode === 'JPY'
                    ? amount
                    : amountUSD
                    ? commaFormat(amountUSD, { numberOfDecimals: 2 })
                    : null}
                </div>
                <div className="text-xs">
                  {currencyMode === 'JPY'
                    ? '円'
                    : isPositionsSameCurrencyUnit
                    ? commonUnit
                    : null}
                </div>
              </div>
            </div>
            <div className="flex ml-auto leading-7 text-bluegray-700">
              <div className="text-lg font-bold mr-1">{ratio}</div>
              <div className="text-xs">%</div>
            </div>
            <div
              className={`flex items-center justify-center ${
                selectedType === 'assets' ? 'w-6' : 'w-10'
              }`}>
              {!isEmptyPositions && (
                <div
                  className={`${
                    isShowPositions ? 'icon-chevron-up' : 'icon-chevron-down'
                  } flex text-2xl text-gray-500`}></div>
              )}
            </div>
          </div>
        </div>
        {selectedType === 'products' &&
          isShowPositions &&
          isHoldForeignStock &&
          !isEmptyPositions && (
            <div
              className={`flex flex-row items-center py-2 px-4 text-bluegray-500 border-bluegray-300 ${
                isShowPositions
                  ? 'border-t border-l border-r'
                  : 'border rounded-b-lg'
              }`}>
              <div className="text-sm">換算レート</div>
              <AboutExchangeRateModalButton isShowMessage={false} />
              <div
                className="ml-auto text-sm cursor-pointer"
                onClick={e => {
                  e.stopPropagation();
                  toggle();
                }}>
                通貨切換
              </div>
              <ToggleButton {...{ currencyMode, toggle }} />
            </div>
          )}
        {isShowPositions && !isEmptyPositions && (
          <>
            {positions.map((position, i) => {
              const isLastLength = positions.length - 1 === i;
              return (
                <React.Fragment key={i}>
                  <PortfolioRatioItemPosition
                    {...{
                      position,
                      isLastLength,
                      currencyMode,
                      selectedType,
                      brokersInfo,
                    }}
                  />
                </React.Fragment>
              );
            })}
          </>
        )}
      </div>
      {(typeKey === AssetType['預金_MRF'] ||
        typeKey === AssetType['その他']) && (
        <div className="flex flex-row justify-end mb-4 text-sm text-bluegray-500">
          <WhatIsIncludeAsstsModalButton
            assetTypeKey={typeKey}
            title={`${title}について`}
          />
        </div>
      )}
      {(typeKey === 'OTHER' || typeKey === 'Other') && (
        <div className="flex mb-4 text-sm text-bluegray-500">
          <div
            className="flex flex-row items-center ml-auto cursor-pointer"
            onClick={showModal}>
            <div className="mr-2">{title}について</div>
            <div className="icon-help_outline ml-1 text-bluegray-400 text-sm"></div>
          </div>
        </div>
      )}
      <WhatIsOtherModal
        {...{
          isShow: isShowModal,
          hide: hideModal,
        }}
      />
    </>
  );
};

const accountInfoList = {
  General: {
    text: '一般',
    color: 'text-blue-450',
    bgColor: 'bg-sky-100',
  },
  Identification: {
    text: '特定',
    color: 'text-bluegray-500',
    bgColor: 'bg-gray-300',
  },
  Nisa: {
    text: 'NISA',
    color: 'text-red-600',
    bgColor: 'bg-red-100',
  },
  DefinedContributionPension: {
    text: '確定拠出年金',
    color: 'text-definedContributionPension',
    bgColor: 'bg-definedContributionPension',
  },
};
